import React, { useState } from 'react';
import { DEFENCE_CHECKLIST } from '../constants';
import { ShieldCheck, AlertCircle, CheckCircle2, XCircle } from 'lucide-react';

interface Scenario {
  id: string;
  threat: string;
  correctControl: string;
}

const SCENARIOS: Scenario[] = [
  { id: '1', threat: "Hackers are trying to guess your employee passwords.", correctControl: "mfa" },
  { id: '2', threat: "Your office PCs haven't been updated in 6 months.", correctControl: "patch" },
  { id: '3', threat: "Ransomware has locked your main server files.", correctControl: "backup" },
  { id: '4', threat: "A user is running programs as 'Administrator' for daily tasks.", correctControl: "leastpriv" },
];

export const Defend: React.FC = () => {
  const [currentScenario, setCurrentScenario] = useState(0);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error', msg: string } | null>(null);
  const [score, setScore] = useState(0);

  const handleMatch = (controlId: string) => {
    const isCorrect = controlId === SCENARIOS[currentScenario].correctControl;
    
    if (isCorrect) {
      setFeedback({ type: 'success', msg: "Correct! That control neutralizes the threat." });
      setScore(s => s + 1);
      setTimeout(() => {
        if (currentScenario < SCENARIOS.length - 1) {
          setCurrentScenario(c => c + 1);
          setFeedback(null);
        } else {
          setFeedback({ type: 'success', msg: "Simulation Complete! You identified all primary controls." });
        }
      }, 2000);
    } else {
      setFeedback({ type: 'error', msg: "That control might help, but it's not the primary defence for this threat. Try again!" });
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 md:p-12">
      <h2 className="text-3xl font-bold mb-2 text-cyber-dark">Defensive Challenge</h2>
      <p className="text-slate-600 mb-8">Can you match the right security control to the specific threat?</p>

      {/* Challenge Area */}
      <div className="bg-slate-900 text-white p-8 rounded-3xl shadow-2xl mb-12 relative overflow-hidden">
        <div className="absolute top-0 right-0 p-4 font-mono text-xs text-slate-500">SCENARIO {currentScenario + 1}/{SCENARIOS.length}</div>
        
        <div className="flex items-center gap-4 mb-6">
          <div className="bg-red-500/20 p-3 rounded-full">
            <AlertCircle className="text-red-500" size={32} />
          </div>
          <h3 className="text-2xl font-bold">THREAT DETECTED:</h3>
        </div>
        
        <p className="text-xl font-medium mb-12 leading-relaxed">
          "{SCENARIOS[currentScenario].threat}"
        </p>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {DEFENCE_CHECKLIST.map(control => (
            <button
              key={control.id}
              onClick={() => handleMatch(control.id)}
              disabled={!!feedback && feedback.type === 'success'}
              className="bg-slate-800 hover:bg-slate-700 p-4 rounded-xl text-center text-sm font-bold border border-slate-700 transition-all hover:scale-105 active:scale-95 disabled:opacity-50"
            >
              {control.title}
            </button>
          ))}
        </div>

        {feedback && (
          <div className={`mt-8 p-4 rounded-xl flex items-center gap-3 animate-fade-in ${
            feedback.type === 'success' ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'bg-red-500/20 text-red-400 border border-red-500/30'
          }`}>
            {feedback.type === 'success' ? <CheckCircle2 size={20} /> : <XCircle size={20} />}
            <p className="font-bold">{feedback.msg}</p>
          </div>
        )}
      </div>

      <h2 className="text-2xl font-bold mb-6 text-cyber-dark">Defensive Reference</h2>
      <div className="grid gap-6">
        {DEFENCE_CHECKLIST.map((item) => (
          <div key={item.id} className="bg-white p-6 rounded-xl shadow border-l-8 border-green-500 flex items-start gap-4">
            <div className="bg-green-100 p-3 rounded-full">
              <ShieldCheck className="text-green-600" size={24} />
            </div>
            <div>
              <h3 className="text-xl font-bold text-slate-800">{item.title}</h3>
              <p className="text-slate-600 mt-1">{item.desc}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
